'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useSupabase } from '@/providers/supabase-provider';
import { SupabaseConfigError } from '@/lib/supabase';

export default function ResetPasswordPage() {
  const supabase = useSupabase();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email.toLowerCase().trim(),
        {
          redirectTo: `${window.location.origin}/auth/callback?next=/update-password`,
        }
      );

      if (resetError) {
        setError('Erro ao enviar email de recuperação. Tente novamente.');
        return;
      }

      setSuccess(true);
    } catch (err) {
      console.error('Reset password error:', err);
      if (err instanceof SupabaseConfigError) {
        setError('Serviço temporariamente indisponível. Tente novamente em alguns minutos.');
      } else {
        setError('Erro de conexão. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <main className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <div className="bg-green-500/20 border border-green-500/50 rounded-2xl p-8 text-center">
            <div className="text-4xl mb-4">📧</div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Verifique seu email!
            </h2>
            <p className="text-gray-300 mb-6">
              Enviamos um link de recuperação para <strong>{email}</strong>
            </p>
            <p className="text-gray-400 text-sm">
              Clique no link do email para redefinir sua senha.
            </p>
            <Link
              href="/login"
              className="inline-block mt-6 text-cyan-400 hover:underline"
            >
              Voltar para o login
            </Link>
          </div>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <Link href="/" className="flex items-center justify-center mb-8">
          <img 
            src="/logo-atende-turbo.png" 
            alt="Atende Turbo" 
            className="h-16 w-auto"
          />
        </Link>

        {/* Card */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
          <h1 className="text-2xl font-bold text-white text-center mb-2">
            Recuperar senha
          </h1>
          <p className="text-gray-400 text-center mb-8">
            Digite seu email para receber um link de recuperação
          </p>

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm text-gray-300 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition"
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl text-white font-bold hover:opacity-90 transition disabled:opacity-50"
            >
              {loading ? 'Enviando...' : 'Enviar link de recuperação'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400">
              Lembrou a senha?{' '}
              <Link href="/login" className="text-cyan-400 hover:underline">
                Fazer login
              </Link>
            </p>
          </div>
        </div>

        {/* Back to home */}
        <p className="text-center mt-6">
          <Link href="/" className="text-gray-500 hover:text-gray-300 transition">
            ← Voltar para o início
          </Link>
        </p>
      </motion.div>
    </main>
  );
}
